import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  formatDate,
  formatMoney,
  PROPERTY_SYSTEM_TYPE_LABELS,
} from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  Input,
  Select,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";

const TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  active: "success",
  paused: "warning",
  ended: "neutral",
};
const LABEL: Record<string, string> = {
  draft: "Entwurf",
  active: "Aktiv",
  paused: "Pausiert",
  ended: "Beendet",
};
const INTERVAL: Record<string, string> = {
  monthly: "monatlich",
  quarterly: "quartalsweise",
  semiannual: "halbjährlich",
  yearly: "jährlich",
};

const STATUS_OPTIONS = [
  { value: "", label: "Alle Status" },
  ...Object.entries(LABEL).map(([value, label]) => ({ value, label })),
];
const INTERVAL_OPTIONS = [
  { value: "", label: "Alle Intervalle" },
  ...Object.entries(INTERVAL).map(([value, label]) => ({ value, label })),
];

export function Contracts() {
  const navigate = useNavigate();
  const list = trpc.contract.list.useQuery();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [interval, setInterval] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [systemType, setSystemType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const properties = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    for (const c of list.data ?? []) {
      if (c.property)
        map.set(c.property.id, { id: c.property.id, label: c.property.label });
    }
    return [...map.values()];
  }, [list.data]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (list.data ?? []).filter((c) => {
      if (s) {
        const hay = [
          c.number,
          c.title,
          c.customer.name,
          c.property?.label,
          c.property?.addressStreet,
          c.property?.addressCity,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (status && c.status !== status) return false;
      if (interval && c.interval !== interval) return false;
      if (propertyId && c.property?.id !== propertyId) return false;
      if (systemType && c.property?.systemType !== systemType) return false;
      if (from && (!c.nextRunDate || new Date(c.nextRunDate) < new Date(from)))
        return false;
      if (
        to &&
        (!c.nextRunDate || new Date(c.nextRunDate) > new Date(`${to}T23:59:59`))
      )
        return false;
      return true;
    });
  }, [list.data, search, status, interval, propertyId, systemType, from, to]);

  const { sorted, sort, toggle } = useSort(filtered, {
    customer: (c) => c.customer.name,
    number: (c) => c.number,
    property: (c) => c.property?.label,
    interval: (c) => INTERVAL[c.interval] ?? c.interval,
    next: (c) => (c.status === "active" ? c.nextRunDate : null),
    status: (c) => LABEL[c.status] ?? c.status,
    amount: (c) => c.amountNetCents,
  });

  const hasFilters =
    search || status || interval || propertyId || systemType || from || to;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Verträge"
        actions={
          <Button variant="primary" onClick={() => navigate("/vertraege/neu")}>
            <Plus className="w-4 h-4" /> Neuer Vertrag
          </Button>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Betreff, Nummer, Kunde, Objekt …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          fullWidth={false}
          className="w-40"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={STATUS_OPTIONS}
        />
        <Select
          fullWidth={false}
          className="w-40"
          value={interval}
          onChange={(e) => setInterval(e.target.value)}
          options={INTERVAL_OPTIONS}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          options={[
            { value: "", label: "Alle Objekte" },
            ...properties.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={systemType}
          onChange={(e) => setSystemType(e.target.value)}
          options={[
            { value: "", label: "Alle Anlagentypen" },
            ...Object.entries(PROPERTY_SYSTEM_TYPE_LABELS).map(
              ([value, label]) => ({ value, label }),
            ),
          ]}
        />
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          title="Fälligkeit von"
        />
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          title="Fälligkeit bis"
        />
        {hasFilters && (
          <button
            className="text-xs text-text-tertiary hover:text-text-primary"
            onClick={() => {
              setSearch("");
              setStatus("");
              setInterval("");
              setPropertyId("");
              setSystemType("");
              setFrom("");
              setTo("");
            }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !filtered.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {hasFilters ? "Keine Treffer." : "Noch keine Verträge."}
          </div>
        ) : (
          <>
            {/* Mobil: Karten statt Tabelle (AUDIT-2026-10-01-UX.md, Punkt 3.3). */}
            <div className="md:hidden divide-y divide-border-subtle">
              {sorted.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/vertraege/${c.id}`)}
                  className="w-full text-left px-4 py-3 flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-text-tertiary">
                      {c.number ?? "—"}
                    </span>
                    <Badge tone={TONE[c.status] ?? "neutral"}>
                      {LABEL[c.status] ?? c.status}
                    </Badge>
                  </div>
                  <div className="font-medium text-text-primary">{c.title}</div>
                  <div className="text-text-tertiary text-xs">
                    {c.customer.name}
                    {c.property ? ` · ${c.property.label}` : ""}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-sm">
                    <span className="text-text-tertiary text-xs">
                      {INTERVAL[c.interval] ?? c.interval}
                      {c.status === "active"
                        ? ` · nächste Fälligkeit ${formatDate(c.nextRunDate as unknown as string)}`
                        : ""}
                    </span>
                    <span className="text-money text-text-primary">
                      {formatMoney(c.amountNetCents)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <table className="data-table hidden md:table">
              <thead>
                <tr>
                  <SortTh
                    label="Kunde / Betreff"
                    sortKey="customer"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Nummer"
                    sortKey="number"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Objekt"
                    sortKey="property"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Intervall"
                    sortKey="interval"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Nächste Fälligkeit"
                    sortKey="next"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Status"
                    sortKey="status"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Betrag netto"
                    sortKey="amount"
                    sort={sort}
                    onSort={toggle}
                    className="text-right"
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/vertraege/${c.id}`)}
                  >
                    <td className="text-text-primary">
                      <div className="font-medium">{c.customer.name}</div>
                      <div className="text-text-tertiary text-xs">
                        {c.title}
                      </div>
                    </td>
                    <td className="font-mono text-text-secondary">
                      {c.number ?? "—"}
                    </td>
                    <td className="text-text-tertiary text-xs">
                      {c.property?.label ?? "—"}
                    </td>
                    <td className="text-text-secondary">
                      {INTERVAL[c.interval] ?? c.interval}
                    </td>
                    <td className="text-text-secondary">
                      {c.status === "active"
                        ? formatDate(c.nextRunDate as unknown as string)
                        : "—"}
                    </td>
                    <td>
                      <Badge tone={TONE[c.status] ?? "neutral"}>
                        {LABEL[c.status] ?? c.status}
                      </Badge>
                    </td>
                    <td className="text-right text-money text-text-primary">
                      {formatMoney(c.amountNetCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
